/**
 * Ontology-oriented element discovery for accessible WCs.
 * Uses Revit bulk properties (viewer or sidecar pattern) — not per-model regex forks.
 */
import {
  ACCESSIBLE_ROOM_RX,
  type AccessibleWcCandidate,
  WC_NEG_RX,
  WC_POS_RX,
} from "./types";

function propValue(
  props: Array<{ displayName: string; displayValue: string | number }>,
  name: string
): string | undefined {
  const hit = props.find(p => p.displayName === name);
  if (!hit) return undefined;
  const v = hit.displayValue;
  return v != null ? String(v) : undefined;
}

async function waitForInstanceTree(
  viewer: Autodesk.Viewing.GuiViewer3D
): Promise<Autodesk.Viewing.InstanceTree> {
  const model = viewer.model;
  let tree = model.getInstanceTree();
  if (tree) return tree;
  await new Promise<void>(resolve => {
    viewer.addEventListener(
      Autodesk.Viewing.OBJECT_TREE_CREATED_EVENT,
      () => resolve(),
      { once: true }
    );
  });
  tree = model.getInstanceTree();
  if (!tree)
    throw new Error("Instance tree unavailable after OBJECT_TREE_CREATED");
  return tree;
}

export async function findAccessibleWaterClosets(
  viewer: Autodesk.Viewing.GuiViewer3D
): Promise<AccessibleWcCandidate[]> {
  const model = viewer.model;
  const tree = await waitForInstanceTree(viewer);
  const allIds: number[] = [];
  const walk = (id: number) => {
    let hasChild = false;
    tree.enumNodeChildren(id, child => {
      hasChild = true;
      walk(child);
    });
    if (!hasChild) allIds.push(id);
  };
  walk(tree.getRootId());

  const nm = (id: number) => tree.getNodeName(id) ?? "";

  const bulkCat = await new Promise<
    Array<{
      dbId: number;
      properties: Array<{ displayName: string; displayValue: string | number }>;
    }>
  >((resolve, reject) => {
    model.getBulkProperties(
      allIds,
      { propFilter: ["Category"] },
      res => resolve(res),
      err => reject(err)
    );
  });

  const catOf = new Map<number, string>();
  for (const row of bulkCat) {
    const cat = propValue(row.properties, "Category") ?? "";
    catOf.set(row.dbId, cat);
  }

  const wcIds = allIds.filter(id => {
    const name = nm(id);
    if (WC_NEG_RX.test(name)) return false;
    // Casita (and many Revit→SVF bundles) omit Category on fixture leaf
    // nodes — match 3D toilet family names directly.
    if (/toilet-domestic/i.test(name)) return true;
    const cat = catOf.get(id) ?? "";
    return /plumbing fixture/i.test(cat) && WC_POS_RX.test(name);
  });

  if (wcIds.length === 0) return [];

  const bulkRoom = await new Promise<
    Array<{
      dbId: number;
      properties: Array<{ displayName: string; displayValue: string | number }>;
    }>
  >((resolve, reject) => {
    model.getBulkProperties(
      wcIds,
      { propFilter: ["Room Name", "Room Number", "Type Name", "Comments"] },
      res => resolve(res),
      err => reject(err)
    );
  });

  const out: AccessibleWcCandidate[] = [];
  for (const row of bulkRoom) {
    const roomName = propValue(row.properties, "Room Name");
    const roomNumber = propValue(row.properties, "Room Number");
    const typeName = propValue(row.properties, "Type Name") ?? "";
    const comments = propValue(row.properties, "Comments") ?? "";
    const accessible =
      (roomName && ACCESSIBLE_ROOM_RX.test(roomName)) ||
      ACCESSIBLE_ROOM_RX.test(typeName) ||
      ACCESSIBLE_ROOM_RX.test(comments);
    if (!accessible) continue;
    out.push({
      dbId: row.dbId,
      roomName,
      roomNumber,
      displayName:
        (roomName ?? "Accessible WC") +
        (roomNumber ? ` #${roomNumber}` : "") +
        ` (${nm(row.dbId)})`,
    });
  }
  return out;
}

/**
 * Domestic toilets for geometry demo — no accessible room/type tagging required.
 * Casita has Bathroom 1 / Primary Bath fixtures but none tagged "accessible".
 */
export async function findDomesticWaterClosets(
  viewer: Autodesk.Viewing.GuiViewer3D
): Promise<AccessibleWcCandidate[]> {
  const tree = await waitForInstanceTree(viewer);
  const allIds: number[] = [];
  const walk = (id: number) => {
    let hasChild = false;
    tree.enumNodeChildren(id, child => {
      hasChild = true;
      walk(child);
    });
    if (!hasChild) allIds.push(id);
  };
  walk(tree.getRootId());

  const nm = (id: number) => tree.getNodeName(id) ?? "";
  const out: AccessibleWcCandidate[] = [];
  for (const id of allIds) {
    const name = nm(id);
    if (!/toilet-domestic/i.test(name) || WC_NEG_RX.test(name)) continue;
    out.push({
      dbId: id,
      displayName: name.replace(/\s*\[\d+\]\s*$/, "").trim() || `WC dbId ${id}`,
    });
  }
  return out;
}

export async function findWaterClosetsForGeometry(
  viewer: Autodesk.Viewing.GuiViewer3D
): Promise<{
  candidates: AccessibleWcCandidate[];
  usedAccessibleFilter: boolean;
}> {
  const accessible = await findAccessibleWaterClosets(viewer);
  if (accessible.length > 0) {
    return { candidates: accessible, usedAccessibleFilter: true };
  }
  return {
    candidates: await findDomesticWaterClosets(viewer),
    usedAccessibleFilter: false,
  };
}
